<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { IconColors, IconType } from '@components/icons/icon-types.js';
import Icon from '@components/icons/icon.vue';
import { TooltipProps } from '@components/tooltip-props.ts';
import { useRouterComposable } from '@router/router-composable.ts';
import { RouterPush } from '@router/interfaces/router-push.ts';
import Tooltip from '@components/tooltip.vue';

const props = withDefaults(
    defineProps<{
        href?: string,
        forceNewTab?: boolean,
        route?: RouterPush,
        icon?: IconType,
        iconColor?: IconColors,
        linkValue?: string,
        highlightOnHover?: boolean,
        bold?: boolean,
        wordBreak?: boolean,
        tooltip?: TooltipProps,
        spread?: boolean,
    }>(),
    {
        forceNewTab: false,
        iconColor: 'default',
        highlightOnHover: true,
        bold: false,
        wordBreak: true,
        spread: false,
    },
);

const rawLink = ref<string|null>(null);

const { resolveRoute, pushRoute } = useRouterComposable();

onMounted(() => {
    if (props.href) {
        rawLink.value = props.href;
        return;
    }
    if (props.route) {
        rawLink.value = resolveRoute(props.route).href;
        return;
    }
    console.error('no route or href given');
});

function onClick(event: MouseEvent) {
    if (props.forceNewTab || event.metaKey || event.ctrlKey || props.href) {
        return;
    }

    event.preventDefault();
    if (props.route) {
        pushRoute(props.route);
    }
    window.scrollTo(0, 0);
}
</script>

<template>
    <a
        :class="[
            $style.link,
            highlightOnHover ? $style.highlight_on_hover : '',
            bold ? $style.bold : '',
            !wordBreak ? $style.no_line_break : '',
            spread ? $style.spread : $style.no_spread,
        ]"
        :href="rawLink ? rawLink : ''"
        :target="forceNewTab ? '_blank' : '_self'"
        @click.stop="onClick"
    >
        <Tooltip v-if="tooltip" v-bind="tooltip" expansion>
            <span :class="$style.link_content">
                <Icon v-if="icon" :class="$style.icon" :icon="icon" :icon-color="iconColor"/>
                <slot>{{ linkValue }}</slot>
            </span>
        </Tooltip>
        <template v-else>
            <span :class="$style.link_content">
                <Icon v-if="icon" :class="$style.icon" :icon="icon" :icon-color="iconColor"/>
                <slot>{{ linkValue }}</slot>
            </span>
        </template>
    </a>
</template>

<style lang="scss" module>
@use '@text' as *;

.link {
    text-decoration: none;
    cursor: pointer;

    * {
        cursor: inherit;
    }
    &.highlight_on_hover:hover {
        color: $primary-text-color;

        .icon {
            color: inherit;
        }
    }
    &.bold {
        font-weight: $weight-bold;
    }
    &.no_line_break {
        white-space: nowrap;
    }
    &.no_spread {
        display: inline-block;
        flex-grow: initial;
        width: initial;
    }
    &.spread {
        display: block;
        flex-grow: 1;
        width: 100%;
    }
    .link_content {
        display: inline-flex;
        height: inherit;
        width: 100%;
        gap: .5em;
    }
    .icon + * {
        margin-left: .5em;
    }
}
</style>
